// Картата „Продукти" на листа „Инвентаризация": лентата с филтрите на изгледа
// и решетката/картите с един keydown handler за стрелките (`onGridKey`).
// Лентата е ЕДИН ред на десктоп: статус Всички / За броене / Преброени /
// Непреброени / С разлика (сегменти с броячи — СЪЩИТЕ числа като в лентите на
// блоковете; „За броене" се вижда само когато има закачени) · групите (чипове,
// скролват настрани, когато не се събират) · „Скрий без наличност"; под
// десктоп групите слизат на свой ред. Филтрите само показват/крият — скрит ред
// остава в сумите и в записа.
// Картата расте до края на екрана и скролва вътре — заглавният ред на
// решетката остава лепнат.
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { SegmentedCounts } from "../../components/list/ListBits";
import { StocktakeWorksheetTable, type StocktakeTableProps } from "./StocktakeWorksheetTable";
import {
  STOCKTAKE_STATUS_FILTERS,
  type StocktakeStatusCounts,
  type StocktakeStatusFilter,
} from "./stocktakeView";

type Props = {
  table: StocktakeTableProps;
  statusFilter: StocktakeStatusFilter;
  // броячите на сегментите — по групата и „Скрий без наличност"
  statusCounts: StocktakeStatusCounts;
  onStatusFilterChange: (filter: StocktakeStatusFilter) => void;
  // Групите на каталога (ключ "" = без група) с броя продукти във всяка
  groups: { key: string; count: number }[];
  groupFilter: string | null;
  onGroupFilterChange: (group: string | null) => void;
  // Скриване на редовете без наличност („Търсено" = 0); преброените остават
  hideZeroStock: boolean;
  onToggleHideZeroStock: () => void;
  onGridKey: (event: React.KeyboardEvent) => void;
};

// Етикетът на сегмента по статус
const STATUS_LABEL_KEY: Record<StocktakeStatusFilter, string> = {
  all: "inventoryPage.stkStatusAll",
  pinned: "inventoryPage.stqStatusPinned",
  counted: "inventoryPage.stkStatusCounted",
  uncounted: "inventoryPage.stkStatusUncounted",
  diff: "inventoryPage.stkStatusDiff",
};
// Празен изглед при избран статус — казва ЗАЩО няма редове
const STATUS_EMPTY_KEY: Record<StocktakeStatusFilter, string> = {
  all: "inventoryPage.wsNoMatch",
  pinned: "inventoryPage.stqEmptyPinned",
  counted: "inventoryPage.stkEmptyCounted",
  uncounted: "inventoryPage.stkEmptyUncounted",
  diff: "inventoryPage.stkEmptyDiff",
};

const chipClass = (on: boolean) =>
  `flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-[11px] py-[5px] text-[12.5px] leading-[normal] outline-none focus-visible:shadow-[0_0_0_3px_var(--color-brand-tint)] ${
    on
      ? "border-brand/45 bg-brand-tint font-semibold text-brand-deep-ink"
      : "border-shade-3 bg-surface text-ink hover:border-shade-5"
  }`;

export const StocktakeProductsCard = ({
  table,
  statusFilter,
  statusCounts,
  onStatusFilterChange,
  groups,
  groupFilter,
  onGroupFilterChange,
  hideZeroStock,
  onToggleHideZeroStock,
  onGridKey,
}: Props) => {
  const { t } = useTranslation();
  const shownCount = table.blocks.reduce((sum, block) => sum + block.rows.length, 0);
  const oneLine = table.isDesktop;
  // Чипове само когато каталогът наистина е разделен на групи
  const showGroups = groups.length > 1;
  const filtered = groupFilter != null || hideZeroStock || statusFilter !== "all";
  return (
    <div className="flex min-h-[360px] flex-1 flex-col overflow-hidden rounded-xl border border-shade-2 bg-panel">
      <div
        className={`flex items-center gap-x-3 gap-y-2 px-3.5 py-2.5 ${oneLine ? "flex-nowrap" : "flex-wrap"}`}
      >
        {/* на телефон сегментите не се събират → скрол настрани */}
        <div className={oneLine ? "shrink-0" : "max-w-full overflow-x-auto"}>
          <SegmentedCounts
            options={STOCKTAKE_STATUS_FILTERS.filter(
              // „За броене" — само когато има закачени (или е избран)
              (key) => key !== "pinned" || statusCounts.pinned > 0 || statusFilter === "pinned",
            ).map((key) => ({
              key,
              label: t(STATUS_LABEL_KEY[key]),
              count: statusCounts[key],
            }))}
            value={statusFilter}
            onChange={onStatusFilterChange}
          />
        </div>
        {/* Групите: на десктоп в същия ред (скрол настрани), иначе на свой ред */}
        {showGroups ? (
          <div
            role="group"
            aria-label={t("inventoryPage.stkGroupsAria")}
            className={`flex min-w-0 items-center gap-1.5 ${
              oneLine
                ? "flex-1 overflow-x-auto py-0.5 [scrollbar-width:thin]"
                : "order-last basis-full flex-wrap"
            }`}
          >
            <button
              type="button"
              aria-pressed={groupFilter == null}
              onClick={() => onGroupFilterChange(null)}
              className={chipClass(groupFilter == null)}
            >
              {t("inventoryPage.stkAllGroups")}
            </button>
            {groups.map((group) => {
              const on = groupFilter === group.key;
              return (
                <button
                  key={group.key || "—"}
                  type="button"
                  aria-pressed={on}
                  onClick={() => onGroupFilterChange(on ? null : group.key)}
                  className={chipClass(on)}
                >
                  {group.key || t("inventoryPage.stocktakeNoGroup")}
                  <span className="font-sheet text-[11px] opacity-70">{group.count}</span>
                </button>
              );
            })}
          </div>
        ) : (
          oneLine && <div className="flex-1" />
        )}
        {!oneLine && <div className="flex-1" />}
        <button
          type="button"
          onClick={onToggleHideZeroStock}
          aria-pressed={hideZeroStock}
          className={`flex shrink-0 cursor-pointer items-center gap-2 rounded-lg border border-shade-3 bg-surface px-3 py-1.5 text-[12.5px] font-semibold leading-[normal] outline-none hover:border-shade-5 focus-visible:shadow-[0_0_0_3px_var(--color-brand-tint)] ${
            hideZeroStock ? "text-brand-deep-ink" : "text-body"
          }`}
        >
          {hideZeroStock ? <EyeOutlined /> : <EyeInvisibleOutlined />}
          {hideZeroStock ? t("inventoryPage.showZeroStock") : t("inventoryPage.hideZeroStock")}
        </button>
      </div>
      <div
        className={`min-h-0 flex-1 overflow-auto border-t border-shade-2 text-[13px] ${
          shownCount ? "bg-surface" : "bg-panel"
        }`}
        onKeyDown={onGridKey}
      >
        <StocktakeWorksheetTable {...table} />
        {/* Филтрите скриха всичко (докато каталогът се зарежда — нищо) */}
        {shownCount === 0 && filtered && (
          <div className="px-6 py-10 text-center text-[13px] text-muted">
            {t(STATUS_EMPTY_KEY[statusFilter])}
          </div>
        )}
      </div>
    </div>
  );
};

export default StocktakeProductsCard;
